/**
 * Bonus Hook
 * Fetches and polls current bonus percentage for deposit page
 *
 * Polls every 60 seconds to catch admin bonus changes while user is on deposit page
 */

import { useEffect, useRef, useCallback } from 'react';
import { useApi } from './useApi';
import { walletAPI } from '@/services/api';
import type { WalletBonusResponse } from '@/types';
import { BONUS_POLLING_INTERVAL_MS } from '@/types';

// =============================================================================
// Types
// =============================================================================

export interface UseBonusOptions {
  /** Whether to enable polling (default: true) */
  enablePolling?: boolean;
  /** Polling interval in ms (default: 60000 = 1 minute) */
  pollingIntervalMs?: number;
  /** Callback when bonus changes */
  onBonusChange?: (newPercentage: number) => void;
}

export interface UseBonusReturn {
  /** Current bonus percentage (0-100) */
  bonusPercentage: number;
  /** Whether first-time bonus is enabled */
  firstTimeBonusEnabled: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Manually refresh bonus */
  refresh: () => Promise<WalletBonusResponse | null>;
  /** Stop polling */
  stopPolling: () => void;
  /** Start polling */
  startPolling: () => void;
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function useBonus(options: UseBonusOptions = {}): UseBonusReturn {
  const {
    enablePolling = true,
    pollingIntervalMs = BONUS_POLLING_INTERVAL_MS,
    onBonusChange,
  } = options;

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(enablePolling);
  const previousBonusRef = useRef<number | null>(null);

  const api = useApi<WalletBonusResponse>(
    () => walletAPI.getBonus(),
    {
      fetchOnMount: true,
      onSuccess: (data) => {
        // Check if bonus changed
        if (previousBonusRef.current !== null && previousBonusRef.current !== data.bonusPercentage) {
          onBonusChange?.(data.bonusPercentage);
        }
        previousBonusRef.current = data.bonusPercentage;
      },
    }
  );

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    isPollingRef.current = true;

    // Start new polling interval
    pollIntervalRef.current = setInterval(() => {
      if (isPollingRef.current) {
        api.execute();
      }
    }, pollingIntervalMs);
  }, [api, pollingIntervalMs]);

  // Start polling on mount, stop on unmount
  useEffect(() => {
    if (enablePolling) {
      startPolling();
    }

    return () => {
      stopPolling();
    };
  }, [enablePolling, startPolling, stopPolling]);

  return {
    bonusPercentage: api.data?.bonusPercentage ?? 0,
    firstTimeBonusEnabled: api.data?.firstTimeBonusEnabled ?? false,
    isLoading: api.isLoading,
    error: api.error,
    refresh: api.execute,
    stopPolling,
    startPolling,
  };
}
